import { HardwareDashboardPathsSchema, HardwareValueHistorySchema } from './jobs.js';
import { HardwareCheckSelectionSchema, HardwareCheckAuditSchema } from './hardware-checks.js';
/**
 * Storage envelope + status-transition policy for FieldReports.
 *
 * The backend's FieldStore persists submissions/photos wrapped in these
 * envelopes, and the admin PWA reads the same shapes back through
 * /api/admin/*. Defining them here (as Zod schemas) means both sides
 * validate the wire shape instead of hand-maintaining mirrored interfaces.
 */
import { z } from 'zod';
import { HardwareLabelProvenanceSchema } from './hardware-suggestions.js';
import { AdminAuditEntrySchema, AdminReportStatusSchema, } from './admin.js';
import { FieldReportSchema, GpsCoordsSchema, InspectorIdSchema } from './field.js';
import { GeneratedArtefactSchema } from './generated-artifacts.js';
/**
 * Persisted state of a submission. Mirrors the IndexedDB shape on the client
 * so the round-trip is symmetric.
 */
export const HardwareConflictSchema = z.object({
    id: z.string(),
    path: z.string(),
    label: z.string(),
    dashboardValue: z.unknown(),
    dashboardEntered: z.object({ by: z.string(), at: z.string() }).optional(),
    fieldValue: z.unknown(),
    detectedAt: z.string(),
    resolution: z
        .object({
        by: z.string(),
        at: z.string(),
        choice: z.enum(['dashboard', 'field', 'edited']),
        value: z.unknown().optional(),
    })
        .optional(),
});
export const StoredReportSchema = z.object({
    jobId: z.string(),
    /** Revision key. A supervisor edit mints a new one; it is not the inspector's submit clock. */
    submittedAt: z.string(),
    /**
     * When the inspector submitted. Copied onto later revisions so an edit's
     * new `submittedAt` key is not shown as the submit time. Older rows omit
     * it; readers fill it from the oldest revision key.
     */
    inspectorSubmittedAt: z.string().optional(),
    inspectorId: InspectorIdSchema,
    status: AdminReportStatusSchema,
    portalApprovedAt: z.string().optional(),
    submission: FieldReportSchema,
    ackedAt: z.string().optional(),
    lastError: z.string().optional(),
    /** Status-change audit trail. Empty until the first transition. */
    audit: z.array(AdminAuditEntrySchema).optional(),
    /** Immutable PDFs generated from this exact report revision. */
    artefacts: z.array(GeneratedArtefactSchema).optional(),
    hardwareLabelReviews: z.array(HardwareLabelProvenanceSchema).optional(),
    hardwarePhotoReviews: z.array(HardwareCheckAuditSchema).optional(),
    hardwarePhotoReviewWarnings: z.array(z.string()).optional(),
    /**
     * Where the hardware on this revision came from. Set when a field resubmit
     * merges an admin layer: `admin` means the admin values were kept as saved,
     * `merged` means the inspector's values filled blanks the admin left empty,
     * `field` means no admin hardware layer was carried forward.
     */
    /** Job hardware edit incorporated by submission, including a concurrent re-merge. */
    hardwareJobEditedAt: z.string().optional(),
    /** Ownership snapshot in this revision’s hardware order; absent means unknown legacy provenance. */
    hardwareDashboardPaths: HardwareDashboardPathsSchema.optional(),
    hardwareDashboardHistory: HardwareValueHistorySchema.optional(),
    hardwareConflicts: z.array(HardwareConflictSchema).optional(),
    /**
     * Cover-sheet disagreements between a dashboard job cover and a later field
     * report. Same shape and resolution rules as hardware conflicts.
     */
    coverConflicts: z.array(HardwareConflictSchema).optional(),
    hardwareSource: z.enum(['admin', 'field', 'merged']).optional(),
    /**
     * Disagreements from a field resubmit. The admin value was kept; the line
     * says what the inspector sent. Shown on the report for the reviewer.
     */
    adminMergeConflicts: z.array(z.string().min(1).max(500)).max(200).optional(),
});
/**
 * Supervisor edit request. The original submission remains immutable; the
 * backend validates this replacement FieldReport and stores it as a new
 * pending revision with an audit entry pointing back to the edited revision.
 */
export const AdminReportEditRequestSchema = z.object({
    submission: FieldReportSchema,
    hardwareChecks: z.array(HardwareCheckSelectionSchema).max(100).optional(),
    note: z.string().trim().min(1).max(2000).optional(),
});
export const AdminReportEditResponseSchema = z.object({
    ok: z.literal(true),
    revision: StoredReportSchema,
});
/** Clockwise step a caller may ask to apply. The stored file is what turns. */
export const PhotoQuarterTurnSchema = z.union([z.literal(90), z.literal(180), z.literal(270)]);
/** Net clockwise angle recorded on a photo after one or more rotates. */
export const PhotoRotationDegreesSchema = z.union([
    z.literal(0),
    z.literal(90),
    z.literal(180),
    z.literal(270),
]);
/**
 * Persisted state of a photo. The blob lives in storage (S3 or local fs);
 * this manifest tracks where + the metadata.
 */
export const StoredPhotoManifestSchema = z.object({
    /** Server-recorded attachment origin; absent on older evidence. */
    attachmentSource: z.enum(['manual', 'dropbox-import']).optional(),
    jobId: z.string(),
    photoId: z.string(),
    promptKey: z.string(),
    filename: z.string(),
    mimeType: z.string(),
    byteSize: z.number().int().nonnegative(),
    capturedAt: z.string(),
    /** Upload time is retained for compatibility but is not a capture timestamp. */
    captureTimeSource: z.enum(['client', 'exif', 'upload']).optional(),
    /**
     * S3 key, or local fs path relative to the bucket root. Keyed by photo id,
     * NOT by promptKey — so re-filing a photo never moves the blob.
     */
    storageKey: z.string(),
    uploadedAt: z.string(),
    capturedGps: GpsCoordsSchema.optional(),
    /**
     * How many times this photo has been re-filed under a different promptKey.
     * Absent or 0 means it is still filed as it was uploaded.
     *
     * QA treats the manifest as write-once everywhere else. Re-filing is the one
     * operation that could make a failing submission look complete, so the fact
     * that it happened is carried on the manifest itself — enough for the QA
     * screen to badge a photo without a join. WHO changed it, WHEN, and what the
     * full sequence was lives in the audit log (`photo.rekey`), which is
     * append-only and already carries actor and correlation id. Deliberately not
     * duplicated here: two copies of one history eventually disagree.
     */
    promptKeyRevision: z.number().int().nonnegative().optional(),
    /** The promptKey immediately before the most recent re-file. */
    previousPromptKey: z.string().optional(),
    /**
     * Net clockwise quarter-turns baked into the stored file. Absent until the
     * first rotate. 0 means it was turned and later turned back to the start.
     */
    rotationDegrees: PhotoRotationDegreesSchema.optional(),
    /** When the stored file was last rotated. */
    rotatedAt: z.string().optional(),
    /**
     * Whether the last rotate kept a field evidence footer at the bottom.
     * False means the whole frame, strip included, was turned.
     */
    rotationFooterPreserved: z.boolean().optional(),
    /**
     * Pixel height of the evidence footer the Field PWA stamped under the
     * photo. When present, rotate trusts it instead of detecting the strip.
     */
    evidenceFooterPx: z.number().int().positive().optional(),
    /**
     * Storage key of the pre-rotation original. Written once, on the first
     * rotate, and never replaced.
     */
    originalStorageKey: z.string().optional(),
});
/**
 * Status-transition matrix (see the rules documented in admin.ts).
 * `failed` is reserved for the sync queue; `sent` is a legacy alias for
 * `pending` — the tech head never transitions to/from either manually.
 */
export const ADMIN_TRANSITIONS = {
    pending: ['approved', 'information-required', 'revisit-required'],
    // Every QA outcome is reversible: review is a judgement, and a reviewer who
    // learns something new must be able to change it. Nothing here is terminal
    // any more — the terminal states (Submitted to CER, Complete) belong to the
    // job now, which is the record that actually knows.
    approved: ['pending', 'information-required', 'revisit-required'],
    'information-required': ['pending', 'approved', 'revisit-required'],
    'revisit-required': ['pending', 'approved', 'information-required'],
};
/** Whether a manual admin status transition `from` → `to` is allowed. */
export function isAllowedTransition(from, to) {
    return ADMIN_TRANSITIONS[from].includes(to);
}
//# sourceMappingURL=store.js.map